import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "jsonfirst_civ_conversations";
const MAX_HISTORY_FOR_API = 20;

function genId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "c-" + Math.random().toString(36).slice(2, 12);
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && Array.isArray(d.conversations)) return d;
  } catch (_) {}
  return null;
}

function saveState(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch (_) {}
}

function newConversation(mode = "casual") {
  const now = Date.now();
  return {
    id: genId(),
    title: "Nouvelle discussion",
    mode,
    createdAt: now,
    updatedAt: now,
    messages: [],
  };
}

function deriveTitle(conv) {
  const first = conv.messages.find((m) => m.role === "user");
  if (first && first.content) return first.content.slice(0, 32);
  return "Nouvelle discussion";
}

export default function useConversations() {
  const [state, setState] = useState(() => {
    const loaded = loadState();
    if (loaded && loaded.conversations && loaded.conversations.length > 0) {
      // v3.10.0 — Retour dans le PWA après inactivité (>30 min) : nouvelle
      // conversation propre en tête, l'historique reste dans la sidebar.
      const FRESH_SESSION_MS = 30 * 60 * 1000;
      const active = loaded.conversations.find((c) => c.id === loaded.activeId) || loaded.conversations[0];
      if (active && active.messages.length > 0 && Date.now() - (active.updatedAt || 0) > FRESH_SESSION_MS) {
        const fresh = newConversation("casual");
        return { conversations: [fresh, ...loaded.conversations], activeId: fresh.id };
      }
      return loaded;
    }
    const first = newConversation("casual");
    return { conversations: [first], activeId: first.id };
  });

  useEffect(() => {
    saveState(state);
  }, [state]);

  const active =
    state.conversations.find((c) => c.id === state.activeId) ||
    state.conversations[0];

  const create = useCallback((mode = "casual") => {
    const conv = newConversation(mode);
    setState((prev) => ({
      conversations: [conv, ...prev.conversations],
      activeId: conv.id,
    }));
    return conv;
  }, []);

  const select = useCallback((id) => {
    setState((prev) => ({ ...prev, activeId: id }));
  }, []);

  // v1.17.0 — Patch persona mode of an existing conversation.
  const updateMode = useCallback((id, nextMode) => {
    if (!nextMode) return;
    setState((prev) => ({
      ...prev,
      conversations: prev.conversations.map((c) => c.id === id ? { ...c, mode: nextMode } : c),
    }));
  }, []);

  const deleteOne = useCallback((id) => {
    setState((prev) => {
      const remaining = prev.conversations.filter((c) => c.id !== id);
      if (remaining.length === 0) {
        const fresh = newConversation("casual");
        return { conversations: [fresh], activeId: fresh.id };
      }
      const activeId = prev.activeId === id ? remaining[0].id : prev.activeId;
      return { conversations: remaining, activeId };
    });
  }, []);

  const appendMessage = useCallback((id, msg) => {
    setState((prev) => {
      const updatedList = prev.conversations.map((c) => {
        if (c.id !== id) return c;
        const nextMessages = [...c.messages, msg];
        const next = {
          ...c,
          messages: nextMessages,
          updatedAt: Date.now(),
        };
        next.title = deriveTitle(next);
        return next;
      });
      // bubble active conv to top on write
      const updated = updatedList.find((c) => c.id === id);
      const others = updatedList.filter((c) => c.id !== id);
      return {
        ...prev,
        conversations: updated ? [updated, ...others] : updatedList,
      };
    });
  }, []);

  // Patch an existing message by index (for streaming token-by-token updates).
  const updateMessage = useCallback((id, index, patch) => {
    setState((prev) => ({
      ...prev,
      conversations: prev.conversations.map((c) => {
        if (c.id !== id) return c;
        const messages = c.messages.slice();
        if (index < 0 || index >= messages.length) return c;
        const target = messages[index];
        messages[index] = typeof patch === 'function' ? patch(target) : { ...target, ...patch };
        return { ...c, messages, updatedAt: Date.now() };
      }),
    }));
  }, []);

  // Remove a message by index (used to clean orphaned streaming bubbles).
  const removeMessage = useCallback((id, index) => {
    setState((prev) => ({
      ...prev,
      conversations: prev.conversations.map((c) => {
        if (c.id !== id) return c;
        if (index < 0 || index >= c.messages.length) return c;
        const messages = c.messages.slice();
        messages.splice(index, 1);
        return { ...c, messages, updatedAt: Date.now() };
      }),
    }));
  }, []);

  const clearAll = useCallback(() => {
    const fresh = newConversation("casual");
    setState({ conversations: [fresh], activeId: fresh.id });
  }, []);

  const buildApiHistory = useCallback((conv) => {
    if (!conv || !conv.messages) return [];
    return conv.messages
      .slice(-MAX_HISTORY_FOR_API)
      .filter((m) => m.role === "user" || m.role === "agent")
      .map((m) => ({
        role: m.role === "agent" ? "assistant" : "user",
        content: (m.content || "").slice(0, 4000),
      }));
  }, []);

  return {
    list: state.conversations,
    activeId: state.activeId,
    active,
    create,
    select,
    updateMode,
    delete: deleteOne,
    appendMessage,
    updateMessage,
    removeMessage,
    clearAll,
    buildApiHistory,
  };
}
